import { useState } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import useFetchData from '../../../../hooks/UseFetchData'
import Table from '../../../../components/his/invoices/Table'
import PageHeader from '../../../../shared/PageHeader'
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaFileExcel,
  FaFilePdf,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import moment from 'moment'
import { useSelector } from 'react-redux'
import * as XLSX from 'xlsx'
import { ColumnGroup } from 'primereact/columngroup'
import { Column } from 'primereact/column'
import { Row as ColumnRow } from 'primereact/row'

import CustomButton from '../../../../shared/CustomButton'
import { groupPayers } from '../../../../utils/GroupByItem'
import { formatDateTime } from '../../../../utils/FormateDateTime'
import Filters from '../../../../components/his/doctorSummaryReport/Filters'
import { AmiriRegBase64 } from '../../../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../../../assets/fonts/AmiriB'

const columns = t => [
  {
    header: t('Doctor'),
    bodyCellClass: 'border !border-gray-300 !text-12-gray',
    headerTitleClass: '!text-14-dark',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Doctor}
      </span>
    ),
  },
  {
    header: t('Medication Name'),
    bodyCellClass: 'border !border-gray-300 !text-12-gray',
    headerTitleClass: '!text-14-dark',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.MedicationName}
      </span>
    ),
  },
  {
    header: t('Quantity'),
    bodyCellClass: 'border !border-gray-300 !text-12-gray',
    headerTitleClass: '!text-14-dark',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Quantity}
      </span>
    ),
  },
  {
    header: t('Insurance Coverage'),
    bodyCellClass: 'border !border-gray-300 !text-12-gray',
    headerTitleClass: '!text-14-dark',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.InsuranceCoverage}
      </span>
    ),
  },
]

export const exportInvoicesToExcel = (
  data,
  t,
  fileName = 'user-collection-daily-report.xlsx'
) => {
  const formatted = data?.map(row => {
    const isSeparator = row?.separator
    const isGroupHeader = row?.isGroupHeader

    return {
      [t('key')]: isGroupHeader ? t('Header') : isSeparator ? t('Total') : '----',
    }
  })

  const worksheet = XLSX.utils.json_to_sheet(formatted)
  worksheet['!cols'] = new Array(Object.keys(formatted[0]).length).fill({
    wch: 20,
  })
  worksheet['!rows'] = new Array(formatted.length).fill({ hpt: 22 })

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    'User Collection Detailed Report'
  )
  XLSX.writeFile(workbook, fileName)
}

export const exportInvoicesToPDF = (
  payload,
  t,
  payloadData,
  fileName = 'user-collection-summary-report.pdf'
) => {
  const doc = new jsPDF('landscape')

  // Add Arabic font (font is added from Base64 string)
  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64) // Embed the Base64-encoded font here
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal') // Register the font with jsPDF
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64) // Embed the Base64-encoded font here
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold') // Register the font with jsPDF
  doc.setFont('Amiri', 'normal') // Set the font to Amiri for Arabic text

  const lineHeight = 4
  const pageWidth = doc.internal.pageSize.getWidth()
  const currentDate = moment().format('dddd Do MMMM, YYYY')

  const start_date = moment(payloadData?.start_date).format('DD/MM/YYYY')
  const end_date = moment(payloadData?.end_date).format('DD/MM/YYYY')
  const user_name = payloadData?.user_name || 'All Users'

  const reportTitle = t('Daily Collections Summary Report')
  const orgName =
    payloadData?.business_en || payloadData?.business_ar || t('Organization')
  const location = payloadData?.address || t('Location')

  let y = 10

  doc.addImage(payloadData.logo, 'PNG', 10, 8, 25, 25)

  // Then shift the text to the right to make space for the logo
  const leftMargin = 40

  // Organization name
  doc.setFontSize(12)
  doc.text(orgName, leftMargin, y + 5)

  // Location
  doc.setFontSize(10)
  doc.text(location, leftMargin, y + lineHeight + 5)

  // Right-to-left (RTL) text for Arabic sections:
  doc.setFontSize(11)
  doc.text(reportTitle, pageWidth - 14, y, { align: 'right' })

  doc.setFontSize(10)
  doc.text(`Printed On: ${currentDate}`, pageWidth - 14, y + lineHeight, {
    align: 'right',
  })

  y += lineHeight * 2
  doc.setDrawColor(0)
  doc.setLineWidth(0.1)
  doc.line(14, y, pageWidth - 14, y)
  y += 6

  const periodText = `${t('Daily collection summary for the period from')} ${start_date || '-'
    } ${t('and upto')} ${end_date || '-'}`
  doc.text(periodText, pageWidth - 14, y, { align: 'right' })

  // Add other text fields as Arabic
  doc.text(
    `${t('Report For')}: ${user_name || t('All Users')}`,
    pageWidth - 14,
    y + lineHeight,
    { align: 'right' }
  )

  // Define the main headers (Row 1)
  const tableHeaders = [[t('Column')]]

  const tableData = []

  payload.forEach(group => {
    group.header &&
      tableData.push([
        {
          content: t(group.header.key),
          styles: { font: 'Amiri', fontStyle: 'bold' },
        },
        {
          content: group.header.value,
          styles: { font: 'Amiri', fontStyle: 'bold' },
        },
      ])

    const { value: footer } = group?.footer

    tableData.push([
      {
        content: group?.footer?.key,
        styles: {
          font: 'Amiri',
          fontStyle: !!footer.grand ? 'bold' : 'normal',
        },
      },
    ])
  })

  // tableData.push([
  //   {
  //     content: 'Net Total',
  //     styles: { font: 'Amiri', fontStyle: !!footer.grand ?'bold':'normal', fontSize: 12 },
  //   },
  // ])

  let spanNext = false
  let spanPaymentMethod = false
  let span2Total = 0
  // let spanSubtotal = false

  // Render the table
  autoTable(doc, {
    startY: y + lineHeight * 3,
    head: tableHeaders,
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: 0,
      fontStyle: 'bold',
      halign: 'left',
      font: 'Amiri',
      fontSize: 8,
      lineWidth: 0.05, // Line width for header grid lines
      lineColor: [0, 0, 0],
    },
    bodyStyles: {
      fillColor: [255, 255, 255], // Background color for body
      lineColor: [0, 0, 0], // Grid line color (black)
      lineWidth: 0.1, // Line width for grid lines
      font: 'Amiri',
      fontSize: 8,
    },
    willDrawCell: data => {
      const rowText = data.cell.text?.[0]
      if (rowText === t('Net Total')) {
        data.cell.styles.fontStyle = 'bold'
        data.cell.styles.fillColor = [240, 240, 240]
      }
    },
    didParseCell: data => {
      if (data.section === 'head') {
        const rowIndex = data.row.index
        const columnIndex = data.column.index

        if (rowIndex === 0 && (columnIndex === 1 || columnIndex === 4)) {
          data.cell.colSpan = 3
        }
      }

      // if (spanNext) {
      //   data.cell.colSpan = 16
      //   spanNext = false
      // }

      // if (spanPaymentMethod) {
      //   data.cell.colSpan = 4
      //   spanPaymentMethod = false
      // }

      // if (span2Total === 2) {
      //   data.cell.colSpan = 7
      //   span2Total = 0
      // }

      // if (spanSubtotal) {
      //   data.cell.colSpan = 3
      //   spanSubtotal = false
      // }

      // if (data.cell.text[0] === 'User ID') {
      //   spanNext = true
      // }

      // if (data.cell.text[0] === 'Payment Method') {
      //   spanPaymentMethod = true
      // }
      // if (data.cell.text[0] === 'Total') {
      //   span2Total++
      // }
      // if (data.cell.text[0].startsWith('Subtotal for')) {
      //   spanSubtotal = true
      // }
    },
  })

  doc.save(fileName)
}

const PrescriptionReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const auth = useSelector(state => state.auth)
  const {
    data: requestData,
    executeFetch: requestFetch,
    loading,
  } = useFetchData()

  const { filters: payloadData, setFilters: setPayloadData } = usePersistedFilters(
    'prescription_report',
    {
      start_date: moment().subtract(1, 'day').toDate(),
    end_date: moment().toDate(),
    license_id: '',
    }
  )

  const requestReport = async (payload = payloadData) => {
    await requestFetch('POST', '/v3/reports/emr/prescription-report', {
      ...payload,
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
    })
  }

  const headerGroup = (
    <ColumnGroup>
      <ColumnRow>
        <Column header={t('Doctor')} />
        <Column header={t('Medication Name')} />
        <Column header={t('Quantity')} />
        <Column header={t('Insurance Coverage')} />
      </ColumnRow>
    </ColumnGroup>
  )

  return (
    <div className="main-page-container !overflow-y-auto scrollbar">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t('Prescription Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <div className="flex items-center w-full justify-between gap-2 mb-2">
        <Filters
          payloadData={payloadData}
          setPayloadData={setPayloadData}
          requestReport={requestReport}
          insturance
        />
        <div className="flex gap-2">
          <CustomButton
            //label={t('Export to Excel')}
            title={t('Export to Excel')}
            buttonType="success"
            icon={FaFileExcel}
            className="w-fit h-[35px]"
            onClick={() => {
              exportInvoicesToExcel(requestData?.data || [], t)
            }}
          />
          <CustomButton
            //label={t('Export to PDF')}
            title={t('Export to PDF')}
            buttonType="danger"
            icon={FaFilePdf}
            className="w-fit h-[35px]"
            onClick={() => {
              exportInvoicesToPDF(requestData?.data || [], t, {
                ...payloadData,
                business_en: auth?.buisnessInfo?.name_en,
                business_ar: auth?.buisnessInfo?.name_ar,
                address: auth?.buisnessInfo?.address,
                logo: auth?.buisnessInfo?.logo,
              })
            }}
          />
        </div>
      </div>

      <Table
        data={requestData?.data || []}
        columns={columns}
        loading={loading}
        headerColumnGroup={headerGroup}
        showGridlines
      />
    </div>
  )
}

export default PrescriptionReport
